/**
 * Ontology field picker dialog for the propensity field-mapping panel.
 *
 * The admin opens this dialog when they want to change the
 * auto-suggested mapping for a single mart column. We render a flat
 * list of every (entity, field) pair in the tenant's ontology, with
 * a search box that filters by name + description. Picking a row
 * returns a `MappingSourceRead` that the parent slots into the
 * column's primary or additional sources.
 *
 * Fetching strategy
 * -----------------
 * - We hit `/relationships/canonical/latest` on first open and cache
 *   the result for the dialog's lifetime. The graph isn't huge
 *   (~few hundred fields total) so client-side filtering is faster
 *   than round-tripping per keystroke.
 * - Fields without source-provenance (user-curated entries; empty
 *   `mappings` array) are dropped from the list — there's nothing to
 *   sync them from.
 *
 * Why a flat field list (vs. a tree)
 * ----------------------------------
 * The admin already knows what they're hunting for ("annual_premium"
 * type queries). A flat list with a search box is one click less than
 * "expand entity → expand field". The entity name shows as a sub-line
 * so you don't lose the parent context when the column name is
 * ambiguous.
 */
import React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseRounded from '@mui/icons-material/CloseRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import { buildApiUrl } from '../../../config';
import { MappingSourceRead } from '../../../services/propensityService';

interface CanonicalFieldRow {
  entity_name: string;
  canonical_entity_id: string;
  field_name: string;
  semantic_type: string;
  data_type: string;
  description: string;
  /** Primary source-tuple for this canonical field. We only show
   * fields that have at least one mapping — anything else can't be
   * synced. */
  primary_source: {
    connector_id: string;
    source_entity: string;
    source_field: string;
  };
  additional_source_count: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSelect: (source: MappingSourceRead) => void;
  tenantId: string;
  /** Title context — e.g. "Pick a source for premium_to_income_ratio". */
  martColumnLabel?: string;
}

const authHeaders = (): Record<string, string> => {
  const token = window.localStorage.getItem('klaris_jwt');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function OntologyBrowserDialog({
  open, onClose, onSelect, tenantId, martColumnLabel,
}: Props) {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [rows, setRows] = React.useState<CanonicalFieldRow[]>([]);
  const [query, setQuery] = React.useState('');

  React.useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    setError(null);
    fetch(
      buildApiUrl(`/api/v1/tenants/${tenantId}/relationships/canonical/latest?tenant_id=${tenantId}`),
      { headers: authHeaders(), cache: 'no-store' } as RequestInit,
    )
      .then(async (r) => {
        if (!r.ok) {
          const data = await r.json().catch(() => ({}));
          const detail = (data && (data.detail || data.message)) || `Request failed (${r.status})`;
          throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
        }
        return r.json();
      })
      .then((data) => {
        if (!alive) return;
        setRows(flatten(data?.canonical_graph || data));
      })
      .catch((e: any) => {
        if (!alive) return;
        setError(e?.message || 'Failed to load ontology');
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, tenantId]);

  const filtered = React.useMemo(() => {
    if (!query.trim()) return rows;
    const q = query.trim().toLowerCase();
    return rows.filter((r) => (
      r.field_name.toLowerCase().includes(q)
      || r.entity_name.toLowerCase().includes(q)
      || (r.description || '').toLowerCase().includes(q)
    ));
  }, [rows, query]);

  const handlePick = (r: CanonicalFieldRow) => {
    const src: MappingSourceRead = {
      canonical_entity_id: r.canonical_entity_id,
      canonical_entity_name: r.entity_name,
      canonical_field_name: r.field_name,
      connector_id: r.primary_source.connector_id,
      source_entity: r.primary_source.source_entity,
      source_field: r.primary_source.source_field,
      confidence: 1.0,
      reasoning: 'Manually selected by admin from the ontology.',
    };
    onSelect(src);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          {martColumnLabel
            ? <>Pick a source for <code>{martColumnLabel}</code></>
            : 'Pick a source field'}
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseRounded fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          fullWidth
          size="small"
          autoFocus
          placeholder="Search by field name, entity, or description"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={20} />
          </Box>
        )}

        {error && (
          <Typography color="error" variant="body2" sx={{ py: 2 }}>
            {error}
          </Typography>
        )}

        {!loading && !error && filtered.length === 0 && (
          <Typography color="text.secondary" variant="body2" sx={{ py: 2 }}>
            {rows.length === 0
              ? 'No ontology found yet. Connect a data source first.'
              : 'No matching fields. Try a different search term.'}
          </Typography>
        )}

        {!loading && !error && filtered.length > 0 && (
          <Stack divider={<Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }} />}>
            {filtered.slice(0, 200).map((r, idx) => (
              <Box
                key={`${r.entity_name}.${r.field_name}.${idx}`}
                role="button"
                tabIndex={0}
                onClick={() => handlePick(r)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') handlePick(r);
                }}
                sx={{
                  px: 1, py: 1.25, cursor: 'pointer', borderRadius: 1,
                  '&:hover': { backgroundColor: 'action.hover' },
                  '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
                }}
              >
                <Stack direction="row" alignItems="baseline" spacing={1}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {r.entity_name}.{r.field_name}
                  </Typography>
                  {(r.semantic_type || r.data_type) && (
                    <Chip
                      size="small"
                      label={r.semantic_type || r.data_type}
                      variant="outlined"
                      sx={{ height: 18, fontSize: 11 }}
                    />
                  )}
                  {r.additional_source_count > 0 && (
                    <Chip
                      size="small"
                      label={`+${r.additional_source_count} more sources`}
                      variant="outlined"
                      color="info"
                      sx={{ height: 18, fontSize: 11 }}
                    />
                  )}
                </Stack>
                {r.description && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                    {r.description.length > 200 ? r.description.slice(0, 197) + '…' : r.description}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  source: <code>{r.primary_source.source_entity}.{r.primary_source.source_field}</code>
                </Typography>
              </Box>
            ))}
            {filtered.length > 200 && (
              <Typography variant="caption" color="text.secondary" sx={{ pt: 1 }}>
                Showing the first 200 matches — refine your search to see more.
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface RawCanonicalGraph {
  entities?: Array<{
    name?: string;
    canonical_entity_id?: string;
    fields?: Array<{
      name?: string;
      semantic_type?: string;
      data_type?: string;
      description?: string;
      mappings?: Array<{
        connector_id?: string;
        source_entity?: string;
        source_field?: string;
      }>;
    }>;
  }>;
}

function flatten(graph: RawCanonicalGraph): CanonicalFieldRow[] {
  const out: CanonicalFieldRow[] = [];
  for (const ent of graph?.entities || []) {
    const ent_name = ent.name || '';
    const ent_id = String(ent.canonical_entity_id || '');
    if (!ent_name) continue;
    for (const f of ent.fields || []) {
      const fname = f.name || '';
      if (!fname) continue;
      const mappings = (f.mappings || []).filter(
        (m) => m && m.source_entity && m.source_field,
      );
      if (mappings.length === 0) continue;
      const m = mappings[0];
      out.push({
        entity_name: ent_name,
        canonical_entity_id: ent_id,
        field_name: fname,
        semantic_type: f.semantic_type || '',
        data_type: f.data_type || '',
        description: (f.description || '').trim(),
        primary_source: {
          connector_id: String(m.connector_id || ''),
          source_entity: m.source_entity || '',
          source_field: m.source_field || '',
        },
        additional_source_count: Math.max(0, mappings.length - 1),
      });
    }
  }
  out.sort((a, b) => (
    a.entity_name.localeCompare(b.entity_name)
    || a.field_name.localeCompare(b.field_name)
  ));
  return out;
}
